'use client';

import useStackedHeader from '@components/cms-components/stacked-header/stacked-header.hook';
import TextBlock from '@components/text-block';
import classNames from 'classnames';
import type { Theme } from 'constants/colors';
import type { ReactElement, ReactNode } from 'react';
import React, { useMemo } from 'react';
import type { TextBlockSize } from 'types/components/text-block';
import type { Layout } from 'types/layout';
import { validateString } from 'utils/validate-string';

import styles from './stacked-header.module.scss';

interface Props {
  readonly callsToAction?: ReactNode | undefined;
  readonly description?: ReactNode | undefined;
  readonly eyebrow?: ReactNode | undefined;
  readonly heading?: ReactNode | undefined;
  readonly layout?: Layout | undefined;
  readonly media?: ReactNode | undefined;
  readonly subHeading?: ReactNode | undefined;
  readonly theme?: Theme | undefined;
  readonly size?: TextBlockSize | undefined;
}

const rootClass = validateString(styles.root);
const mediaWrapperClass = validateString(styles.mediaWrapper);
const contentWrapperClass = validateString(styles.contentWrapper);

function StackedHeader({
  description,
  eyebrow,
  heading,
  layout = 'left',
  subHeading,
  media,
  callsToAction,
  theme,
  size = 'large',
}: Props): ReactElement {
  const { contentClassName, resolvedLayout } = useStackedHeader({
    layout,
    media,
  });

  const contentItems = useMemo(() => {
    const items: ReactNode[] = [];
    if (typeof media !== 'undefined') {
      items.push(
        <div className={mediaWrapperClass} key="media">
          {media}
        </div>,
      );
    }

    items.push(
      <div className={classNames(contentWrapperClass)} key="text">
        <TextBlock
          callsToAction={callsToAction}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          layout={resolvedLayout === 'right' ? 'left' : resolvedLayout}
          size={size}
          subHeading={subHeading}
          theme={theme}
        />
      </div>,
    );

    return layout === 'right' ? items.reverse() : items;
  }, [
    media,
    callsToAction,
    description,
    eyebrow,
    heading,
    resolvedLayout,
    size,
    subHeading,
    theme,
    layout,
  ]);

  return (
    <div className={rootClass}>
      <div className={contentClassName} style={{ position: 'relative' }}>
        {contentItems}
      </div>
    </div>
  );
}

export default StackedHeader;
